import type { SupportedGames } from "@repo/game-configuration"
import type { Locale } from "@repo/i18n"
import type { IntegrationScope } from "@repo/marketplace-admin"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ArrowLeft } from "lucide-react"
import * as React from "react"
import { PageHeader, Section } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { AppAudienceFields } from "@/features/integration-apps/AppAudienceFields"
import {
	cleanLocalized,
	LocalizedDescriptionEditor,
} from "@/features/integration-apps/LocalizedDescriptionEditor"
import { LogoUploadField } from "@/features/integration-apps/LogoUploadField"
import { RedirectUriEditor } from "@/features/integration-apps/RedirectUriEditor"
import { ScopePicker } from "@/features/integration-apps/ScopePicker"
import { ShowOnceTokenDialog } from "@/features/integration-apps/ShowOnceTokenDialog"
import { useCreateIntegrationApp } from "@/queries/marketplace"

export const Route = createFileRoute("/integration-apps/new")({
	component: NewIntegrationAppPage,
})

function slugify(value: string): string {
	return value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 63)
}

function NewIntegrationAppPage() {
	const navigate = useNavigate()
	const createApp = useCreateIntegrationApp()

	const [name, setName] = React.useState("")
	const [slug, setSlug] = React.useState("")
	const [slugTouched, setSlugTouched] = React.useState(false)
	const [shortDescription, setShortDescription] = React.useState<
		Record<string, string>
	>({})
	const [longDescription, setLongDescription] = React.useState<
		Record<string, string>
	>({})
	const [logoUrl, setLogoUrl] = React.useState<string | null>(null)
	const [websiteUrl, setWebsiteUrl] = React.useState("")
	const [scopes, setScopes] = React.useState<IntegrationScope[]>([])
	const [redirectUris, setRedirectUris] = React.useState<string[]>([])
	const [isDevelopment, setIsDevelopment] = React.useState(false)
	const [games, setGames] = React.useState<SupportedGames[]>([])
	const [languages, setLanguages] = React.useState<Locale[]>([])
	const [mintedToken, setMintedToken] = React.useState<string | null>(null)
	const [createdAppId, setCreatedAppId] = React.useState<string | null>(null)

	const effectiveSlug = slugTouched ? slug : slugify(name)
	const canSubmit =
		name.trim().length > 0 && effectiveSlug.length >= 2 && scopes.length > 0

	function submit(event: React.FormEvent) {
		event.preventDefault()
		if (!canSubmit) return

		createApp.mutate(
			{
				name: name.trim(),
				slug: effectiveSlug,
				shortDescription: cleanLocalized(shortDescription),
				longDescription: cleanLocalized(longDescription),
				logoUrl: logoUrl ?? undefined,
				websiteUrl: websiteUrl.trim() || undefined,
				scopes,
				redirectUris,
				isDevelopment,
				games,
				languages,
			},
			{
				onSuccess: (result) => {
					setCreatedAppId(result.app.id)
					setMintedToken(result.plaintextToken)
				},
			},
		)
	}

	function closeTokenDialog() {
		setMintedToken(null)
		if (createdAppId) {
			navigate({
				to: "/integration-apps/$appId",
				params: { appId: createdAppId },
			})
		}
	}

	return (
		<div className="flex max-w-3xl flex-col gap-6 p-6">
			<div className="flex flex-col gap-3">
				<Link
					to="/integration-apps"
					className="inline-flex w-fit items-center gap-1 text-meta text-xs hover:text-foreground"
				>
					<ArrowLeft className="h-3.5 w-3.5" /> Integration apps
				</Link>
				<PageHeader
					title="Mint integration app"
					description="The app starts disabled; a superadmin enables it once the review is done."
				/>
			</div>

			<form onSubmit={submit} className="flex flex-col gap-8">
				<Section label="App">
					<div className="flex flex-col gap-4">
						<div className="flex flex-col gap-1.5">
							<Label htmlFor="name">Name</Label>
							<Input
								id="name"
								value={name}
								onChange={(event) => setName(event.target.value)}
								placeholder="Deckbox Sync"
							/>
						</div>

						<div className="flex flex-col gap-1.5">
							<Label htmlFor="slug">Slug</Label>
							<Input
								id="slug"
								value={effectiveSlug}
								onChange={(event) => {
									setSlugTouched(true)
									// Sanitize to the allowed charset without stripping a
									// trailing hyphen the user is still typing (the full
									// slugify — which trims leading/trailing hyphens — only
									// runs when the slug is auto-derived from the name).
									setSlug(
										event.target.value
											.toLowerCase()
											.replace(/[^a-z0-9-]+/g, "-")
											.replace(/^-+/, "")
											.slice(0, 63),
									)
								}}
								className="font-mono text-sm"
								placeholder="deckbox-sync"
							/>
							<p className="text-xs text-meta">
								Immutable after minting. Appears in the connect URL
								(cardnexus.com/connect/{effectiveSlug || "…"}) and identifies
								the app in rate-limit and metrics keys.
							</p>
						</div>

						<div className="flex flex-col gap-1.5">
							<Label>Logo</Label>
							<LogoUploadField value={logoUrl} onChange={setLogoUrl} />
						</div>

						<div className="flex flex-col gap-1.5">
							<Label htmlFor="websiteUrl">Website URL</Label>
							<Input
								id="websiteUrl"
								value={websiteUrl}
								onChange={(event) => setWebsiteUrl(event.target.value)}
								placeholder="https://partner.example/cardnexus"
							/>
							<p className="text-xs text-meta">
								Where directory visitors go to set up this app's CardNexus
								integration.
							</p>
						</div>

						<AppAudienceFields
							idPrefix="new"
							games={games}
							languages={languages}
							onGamesChange={setGames}
							onLanguagesChange={setLanguages}
						/>

						<div className="flex flex-col gap-1.5">
							<Label>Descriptions</Label>
							<LocalizedDescriptionEditor
								shortDescription={shortDescription}
								longDescription={longDescription}
								onChange={(next) => {
									setShortDescription(next.shortDescription)
									setLongDescription(next.longDescription)
								}}
							/>
						</div>

						<div className="flex items-center justify-between gap-6 border-t pt-4">
							<div className="flex flex-col gap-0.5">
								<Label htmlFor="isDevelopment">Development app</Label>
								<p className="text-xs text-meta">
									Permits http://localhost redirect URIs and shows a development
									badge on the consent screen.
								</p>
							</div>
							<Switch
								id="isDevelopment"
								checked={isDevelopment}
								onCheckedChange={setIsDevelopment}
							/>
						</div>
					</div>
				</Section>

				<Section label="Scope bundle">
					<p className="-mt-1 text-xs text-meta">
						The ceiling for every grant this app receives. Users consent to
						exactly these scopes.
					</p>
					<ScopePicker value={scopes} onChange={setScopes} />
				</Section>

				<Section label="Redirect URIs">
					<p className="-mt-1 text-xs text-meta">
						Where users land after approving the connection.
					</p>
					<RedirectUriEditor value={redirectUris} onChange={setRedirectUris} />
				</Section>

				<div className="flex justify-end gap-2 border-t pt-4">
					<Button type="button" variant="outline" asChild>
						<Link to="/integration-apps">Cancel</Link>
					</Button>
					<Button type="submit" disabled={!canSubmit || createApp.isPending}>
						{createApp.isPending ? "Minting…" : "Mint app"}
					</Button>
				</div>
			</form>

			<ShowOnceTokenDialog
				token={mintedToken}
				label="initial"
				onClose={closeTokenDialog}
			/>
		</div>
	)
}
